import { useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDate, formatDateTime, formatTime } from '../../../lib/format';
import type {
  ComplianceSummary, DashboardAlerts, Pipeline, PlantCoverage, UpcomingAppointment,
} from '@dru/types';
import {
  CalendarClock, CalendarPlus, Circle, CircleCheck, FileBarChart, Hourglass, TriangleAlert,
  UserPlus, UsersRound, X,
} from 'lucide-react';
import {
  Card, CardHeader, DataTable, ErrorState, PhoneFold, StatCard, StatSkeleton, StatStrip,
  StatusBadge, TableSkeleton, cn,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { BRAND } from '@dru/types';
import { CASE_STATUS_LABEL } from './Examinations';

/**
 * The HR landing page, built to be read in five seconds: what to do
 * (three actions), where the workforce stands (five numbers), and what is
 * coming up (next appointments, checkups in progress).
 *
 * Aggregation endpoints, not list calls the browser counts the rows of — a
 * `take: 100` list reduced client-side reports 100 when the real number is
 * 4,000. Every section is fetched only for a role that may read it, and an
 * action is offered only to a role that may take it.
 *
 * Trends, the service mix, upcoming camps and the activity feed left this
 * page (31 Oct 2026) to keep it to what HR acts on; each still has its own
 * screen. No clinical content anywhere: "With the physician: 14" is a queue,
 * not a finding.
 */

const day = (d?: string | null) => (d ? formatDate(d) : '—');
const time = (s: string) => formatTime(s);

/** The record an audit action is about, in the words the rest of the console uses. */
const NOUN: Record<string, string> = {
  employee: 'Employee', examination: 'Medical case', appointment: 'Appointment',
  certificate: 'Certificate', campaign: 'Campaign', consent: 'Consent', user: 'User',
  invoice: 'Invoice', plant: 'Plant',
};

/** Turns `employee.transfer` into "Employee transferred". Shared by the record pages' activity lists. */
export function describe(action: string): string {
  const [domain, verb] = action.split('.');
  const past: Record<string, string> = {
    create: 'created', update: 'updated', transfer: 'transferred',
    deactivate: 'exited', 'status-change': 'status changed', import: 'imported',
    launch: 'launched', activate: 'activated', complete: 'completed',
    cancel: 'cancelled', route: 'given a lab', reschedule: 'rescheduled',
    issue: 'issued', revoke: 'revoked', 'role-assign': 'role assigned',
    generated: 'PDF generated', hire: 'hired', 'no-show': 'marked missed',
  };
  const noun = NOUN[domain ?? ''] ?? (domain ?? '').replace(/^\w/, (c) => c.toUpperCase());
  return `${noun} ${past[verb ?? ''] ?? (verb ?? '').replace(/-/g, ' ')}`.trim();
}

/**
 * A stage's name, in the same words as the case badges on Medical cases.
 * The one override: a signed case is "Physician signed off", because a few
 * signed cases are unfit and "Certified" over those said the opposite.
 */
const stageLabel = (key: string, fallback: string) =>
  key === 'CERTIFIED' ? 'Physician signed off' : (CASE_STATUS_LABEL as Record<string, string>)[key] ?? fallback;


export function Dashboard() {
  const go = useNavigate();
  const { permissions, principal } = useAuth();
  const can = (p: string) => permissions.has(p);

  const canCompliance = can('compliance.read');
  const canCases = can('examination.read');
  const canAppointments = can('appointment.read');

  const none = <T,>() => Promise.resolve(null as T | null);
  const summary = useQuery<ComplianceSummary | null>(
    () => (canCompliance ? api.get<ComplianceSummary>('/compliance/summary') : none()), [canCompliance]);
  const plants = useQuery<PlantCoverage[] | null>(
    () => (canCompliance ? api.get<PlantCoverage[]>('/compliance/by-plant') : none()), [canCompliance]);
  const pipeline = useQuery<Pipeline | null>(
    () => (canCases ? api.get<Pipeline>('/dashboard/pipeline') : none()), [canCases]);
  // Retests sit inside the pipeline's review stage, and the list filters by
  // one status; the split keeps each line's number equal to the list it opens.
  const alerts = useQuery<DashboardAlerts | null>(
    () => (canCases && canCompliance ? api.get<DashboardAlerts>('/dashboard/alerts') : none()), [canCases, canCompliance]);
  const upcoming = useQuery<{ items: UpcomingAppointment[] } | null>(
    () => (canAppointments ? api.get<{ items: UpcomingAppointment[] }>('/dashboard/upcoming?limit=6') : none()),
    [canAppointments]);

  const sum = summary.data;
  // The split of "not covered". Read through these so a console that reaches
  // the browser before the API it was built against still adds up.
  const lapsedOf = (c: ComplianceSummary) => c.lapsed ?? Math.max(0, c.overdue - c.neverExamined);
  const notClearedOf = (c: ComplianceSummary) => c.notCleared ?? 0;
  const pending = pipeline.data?.open ?? sum?.inProgress ?? 0;

  const actions: Array<{ to: string; icon: ReactNode; title: string; hint: string }> = [];
  if (can('employee.create') || can('employee.import')) {
    actions.push({
      to: can('employee.create') ? '/hr/employees/new' : '/hr/employees/import',
      icon: <UserPlus className="h-5 w-5" />, title: 'Add employees', hint: 'One at a time, or import a list',
    });
  }
  if (can('examination.create')) {
    actions.push({
      to: '/hr/examinations?arrange=1',
      icon: <CalendarPlus className="h-5 w-5" />, title: 'Schedule checkup', hint: 'Arrange a medical for an employee',
    });
  }
  if (can('report.export') || can('report.health-read')) {
    actions.push({
      to: '/hr/reports',
      icon: <FileBarChart className="h-5 w-5" />, title: 'View reports',
      hint: can('report.health-read') ? 'Health reports and exports' : 'Coverage, certificates and exports',
    });
  }

  const plantRows = plants.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        hint={sum ? `Where your workforce stands. As at ${formatDateTime(sum.asOf)}.` : 'Where your workforce stands.'}
      />

      {sum && (
        <GettingStarted
          tenantId={principal?.tenant?.id ?? ''}
          can={can}
          hasEmployees={sum.totalEmployees > 0}
          // Any case at all, open or finished, means the first medical was booked.
          hasCases={sum.inProgress > 0 || (pipeline.data?.total ?? 0) > 0
            || sum.covered + lapsedOf(sum) + notClearedOf(sum) > 0}
          // Covered now, or covered once and since lapsed.
          hasCertificate={sum.covered + lapsedOf(sum) > 0}
        />
      )}

      {/* ── What to do ──────────────────────────────────────── */}
      {actions.length > 0 && (
        <nav aria-label="Quick actions" className={cn('grid gap-3', actions.length > 1 && 'sm:grid-cols-2', actions.length > 2 && 'lg:grid-cols-3')}>
          {actions.map((a, i) => <ActionTile key={a.to} {...a} primary={i === 0 && actions.length === 1 ? true : a.to.startsWith('/hr/examinations')} />)}
        </nav>
      )}

      {/* ── Where the workforce stands ──────────────────────── */}
      {canCompliance && (
        <section aria-labelledby="position-h">
          <h2 id="position-h" className="mb-3 text-h4 text-neutral-900">Workforce health coverage</h2>
          {summary.error
            ? <ErrorState title="Coverage could not be loaded" detail={summary.error} onRetry={summary.reload} />
            : summary.loading && !sum ? <StatSkeleton count={5} /> : sum && (
              <StatStrip className="grid-cols-2 md:grid-cols-3 xl:grid-cols-5">
                <StatCard label="Total employees" value={sum.totalEmployees} sub="Active today"
                          icon={<UsersRound className="h-6 w-6" />} onClick={() => go('/hr/employees')} />
                <StatCard label="Covered" value={sum.covered}
                          valueTone="covered" icon={<CircleCheck className="h-6 w-6" />}
                          sub={`${sum.coveragePercent}% have a valid certificate`}
                          onClick={() => go('/hr/certificates')} />
                <StatCard label="Due soon" value={sum.dueSoon}
                          valueTone={sum.dueSoon ? 'dueSoon' : undefined} icon={<CalendarClock className="h-6 w-6" />}
                          sub="Certificate ends within 30 days" onClick={() => go('/hr/compliance/due')} />
                <StatCard label="Not covered" value={sum.overdue}
                          valueTone={sum.overdue ? 'overdue' : undefined} icon={<TriangleAlert className="h-6 w-6" />}
                          sub={`${lapsedOf(sum)} lapsed · ${sum.neverExamined} never examined${notClearedOf(sum) ? ` · ${notClearedOf(sum)} not cleared` : ''}`}
                          onClick={() => go('/hr/compliance')} />
                <StatCard label="Pending checkups" value={pending}
                          valueTone={pending ? 'pending' : undefined} icon={<Hourglass className="h-6 w-6" />}
                          sub="Open, not yet signed off"
                          onClick={canCases ? () => go('/hr/examinations') : undefined} />
              </StatStrip>
            )}
        </section>
      )}

      {/* ── What is coming up ───────────────────────────────── */}
      {(canAppointments || canCases) && (
        <div className={cn('grid items-start gap-6', canAppointments && canCases && 'lg:grid-cols-2')}>
          {canAppointments && (
            <Card className="min-w-0">
              <CardHeader
                title="Upcoming appointments"
                hint="Booked and confirmed, soonest first."
                actions={<CardLink to="/hr/appointments">View all</CardLink>}
              />
              <DataTable<UpcomingAppointment>
                rows={upcoming.data?.items ?? []}
                loading={upcoming.loading}
                error={upcoming.error}
                onRetry={upcoming.reload}
                rowKey={(u) => u.id}
                onRowClick={(u) => go(`/hr/appointments/${u.id}`)}
                empty={{
                  title: 'Nothing booked yet',
                  hint: 'Schedule a checkup and the appointment shows here.',
                }}
                columns={[
                  {
                    key: 'who', header: 'Employee',
                    cell: (u) => (
                      <div className="min-w-0">
                        <div className="truncate font-medium text-neutral-900">{u.order?.employee?.fullName ?? '—'}</div>
                        <div className="truncate text-caption text-neutral-500">{u.partner?.name ?? '—'}</div>
                      </div>
                    ),
                  },
                  {
                    key: 'when', header: 'When', align: 'right',
                    cell: (u) => (
                      <div className="whitespace-nowrap">
                        <div className="text-neutral-900">{day(u.scheduledFor)}</div>
                        <div className="text-caption tabular-nums text-neutral-500">{time(u.scheduledFor)}</div>
                      </div>
                    ),
                  },
                ]}
              />
            </Card>
          )}

          {canCases && (
            pipeline.error
              ? <ErrorState title="Checkups could not be loaded" detail={pipeline.error} onRetry={pipeline.reload} />
              : <CheckupProgress pipeline={pipeline.data} loading={pipeline.loading} retests={alerts.data ? alerts.data.retestsRequired : null} />
          )}
        </div>
      )}

      {/* ── By plant: only when there is more than one ──────── */}
      {canCompliance && plantRows.length > 1 && (
        <section aria-labelledby="plants-h">
          <h2 id="plants-h" className="mb-3 text-h4 text-neutral-900">Coverage by plant</h2>
          <PhoneFold label="coverage by plant">
            <DataTable<PlantCoverage>
              rows={plantRows}
              loading={plants.loading}
              error={plants.error}
              onRetry={plants.reload}
              rowKey={(p) => p.plantId}
              onRowClick={(p) => go(`/hr/employees?plantId=${p.plantId}`)}
              empty={{
                title: 'No plants configured',
                hint: `${BRAND.name} sets up your plants. Contact ${BRAND.name} support to add one.`,
              }}
              columns={[
                {
                  key: 'plant', header: 'Plant',
                  cell: (p) => (
                    <div>
                      <div className="font-medium text-neutral-900">{p.plantName}</div>
                      <div className="font-mono text-caption text-neutral-500">{p.plantCode}</div>
                    </div>
                  ),
                },
                { key: 'total', header: 'Employees', align: 'right', cell: (p) => p.totalEmployees },
                {
                  key: 'coverage', header: 'Covered', align: 'right',
                  cell: (p) => (
                    <StatusBadge tone={p.coveragePercent >= 90 ? 'covered' : p.coveragePercent >= 60 ? 'dueSoon' : 'overdue'}>
                      {p.coveragePercent}%
                    </StatusBadge>
                  ),
                },
                { key: 'dueSoon', header: 'Due soon', align: 'right', cell: (p) => p.dueSoon },
                {
                  // Same definition as the company tile: lapsed + never + not cleared.
                  key: 'notCovered', header: 'Not covered', align: 'right',
                  cell: (p) => <span className={p.overdue > 0 ? 'font-medium text-overdue-600' : ''}>{p.overdue}</span>,
                },
                { key: 'inProgress', header: 'Pending', align: 'right', cell: (p) => p.inProgress },
              ]}
            />
          </PhoneFold>
        </section>
      )}
    </div>
  );
}

/** One of the page's three jobs, as a large target with a line saying what it does. */
function ActionTile({ to, icon, title, hint, primary }: {
  to: string; icon: ReactNode; title: string; hint: string; primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'group flex min-h-[4.5rem] items-center gap-3 rounded-card border p-4 shadow-card transition-colors duration-hover',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
        primary
          ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
          : 'border-neutral-200 bg-white text-neutral-900 hover:border-primary-300 hover:bg-primary-50',
      )}
    >
      <span aria-hidden className={cn(
        'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
        primary ? 'bg-white/15 text-white' : 'bg-primary-50 text-primary-600 group-hover:bg-white',
      )}>
        {icon}
      </span>
      <span className="min-w-0">
        <span className="block text-body font-semibold">{title}</span>
        <span className={cn('block truncate text-small', primary ? 'text-white/80' : 'text-neutral-500')}>{hint}</span>
      </span>
    </Link>
  );
}

function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to}
          className="inline-flex min-h-6 items-center text-small font-medium text-primary-600 hover:text-primary-700 max-md:min-h-10">
      {children} →
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────── */

const DISMISSED = 'dru.hr.gettingStarted.dismissed.';

/**
 * Three steps for a company on its first day: employees in, first medicals
 * booked, certificates back. A new company's dashboard said "Nothing is
 * waiting on you today" over zero employees, which is true and no help.
 *
 * Shown until the company holds its first certificate, or until somebody
 * closes it on this browser. Each step is ticked from the same summary the
 * rest of the page reads, so it cannot disagree with the tiles under it, and
 * a step this login cannot take is shown without a link.
 */
function GettingStarted({ tenantId, can, hasEmployees, hasCases, hasCertificate }: {
  tenantId: string;
  can: (p: string) => boolean;
  hasEmployees: boolean;
  hasCases: boolean;
  hasCertificate: boolean;
}) {
  const key = DISMISSED + tenantId;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(key) === '1'; } catch { return false; }
  });
  if (hasCertificate || dismissed) return null;

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem(key, '1'); } catch { /* closes for this visit only */ }
  };

  const steps: Array<{ label: string; done: boolean; to?: string; also?: { label: string; to: string } }> = [
    {
      label: 'Add your employees', done: hasEmployees,
      to: can('employee.create') ? '/hr/employees/new' : undefined,
      also: can('employee.import') ? { label: 'or import a list', to: '/hr/employees/import' } : undefined,
    },
    {
      label: 'Book their first medicals', done: hasCases,
      to: hasEmployees && can('compliance.read') ? '/hr/compliance?state=NEVER_EXAMINED' : undefined,
    },
    {
      label: 'Certificates arrive once the physician signs', done: false,
      to: hasCases && can('certificate.read') ? '/hr/certificates' : undefined,
    },
  ];

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 px-5 pt-4">
        <h2 className="text-h4 text-neutral-900">Getting started</h2>
        <button type="button" onClick={close} aria-label="Hide getting started"
                className="-mr-2 -mt-1 inline-flex h-8 w-8 items-center justify-center rounded-btn text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700 max-md:h-10 max-md:w-10">
          <X aria-hidden className="h-4 w-4" />
        </button>
      </div>
      <ol className="px-5 pb-4 pt-2">
        {steps.map((st, i) => {
          // The first step not yet done is the one to do now.
          const next = !st.done && steps.slice(0, i).every((p) => p.done);
          return (
            <li key={st.label} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 py-1.5 max-md:min-h-10">
              {st.done
                ? <CircleCheck aria-hidden className="h-4 w-4 shrink-0 text-covered-600" />
                : <Circle aria-hidden className={cn('h-4 w-4 shrink-0', next ? 'text-primary-600' : 'text-neutral-300')} />}
              <span className="sr-only">{st.done ? 'Done:' : `Step ${i + 1}:`}</span>
              {st.to && !st.done ? (
                <Link to={st.to} className={cn('text-body font-medium',
                  next ? 'text-primary-600 hover:text-primary-700' : 'text-neutral-800 hover:text-primary-700')}>
                  {st.label} →
                </Link>
              ) : (
                <span className={cn('text-body', st.done ? 'text-neutral-500 line-through' : 'text-neutral-800')}>{st.label}</span>
              )}
              {st.also && !st.done && (
                <Link to={st.also.to} className="text-small text-neutral-600 underline-offset-2 hover:text-primary-700 hover:underline">
                  {st.also.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </Card>
  );
}

/**
 * Where the open checkups have reached.
 *
 * A bar per stage, widths relative to the largest: a bulge at "With the
 * physician" is a bottleneck with a name. Empty stages are left out, so a
 * company with three cases reads three lines, not nine with six zeroes.
 */
function CheckupProgress({ pipeline, loading, retests }: {
  pipeline?: Pipeline | null; loading: boolean;
  /** Retests inside the review stage; null when this login cannot read the count. */
  retests: number | null;
}) {
  if (loading && !pipeline) {
    return (
      <Card className="min-w-0">
        <CardHeader title="Checkup progress" />
        <div className="px-5 pb-4"><TableSkeleton rows={4} cols={2} /></div>
      </Card>
    );
  }
  if (!pipeline) return null;

  // A signed case is the end of the funnel, not part of what is open.
  const certified = pipeline.certified ?? pipeline.stages.find((st) => st.key === 'CERTIFIED')?.count ?? 0;
  const live = pipeline.open
    ?? pipeline.stages.reduce((n, st) => n + st.count, 0) - certified + pipeline.stalled;
  // Each line links to a list filtered by one status, so its number must be
  // that status's count. The review stage holds retests too: split them out
  // when the count is known, or open the whole list when it is not.
  const lines = pipeline.stages.flatMap((st) => {
    const label2 = stageLabel(st.key, st.label);
    if (st.key !== 'REVIEW_PENDING') return [{ ...st, label2, to: `/hr/examinations?status=${st.key}` }];
    if (retests == null) return [{ ...st, label2: `${label2} (incl. retests)`, to: '/hr/examinations' }];
    const r = Math.min(retests, st.count);
    return [
      { ...st, count: st.count - r, label2, to: '/hr/examinations?status=REVIEW_PENDING' },
      { key: 'RETEST_REQUIRED', label: 'Retest', count: r, label2: stageLabel('RETEST_REQUIRED', 'Retest required'), to: '/hr/examinations?status=RETEST_REQUIRED' },
    ];
  });
  const shown = lines.filter((st) => st.count > 0);
  const peak = Math.max(1, ...shown.map((st) => st.count));

  return (
    <Card className="min-w-0">
      <CardHeader
        title="Checkup progress"
        hint="Where every open checkup has reached. The last line is finished work: the physician has signed. Select a stage to see its cases."
        actions={<CardLink to="/hr/examinations">View all</CardLink>}
      />
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-5 pb-3">
        <span className="text-h3 tabular-nums text-neutral-900">{live.toLocaleString('en-IN')}</span>
        <span className="text-small text-neutral-600">open</span>
        {pipeline.stalled > 0 && (
          <Link to="/hr/examinations?status=NO_SHOW_OPEN"
                className="text-small font-medium text-overdue-600 hover:underline max-md:min-h-10 max-md:inline-flex max-md:items-center">
            {pipeline.stalled} missed — rebook
          </Link>
        )}
      </div>
      {shown.length === 0 ? (
        <p className="px-5 pb-5 text-small text-neutral-500">No open checkups. Schedule one to get started.</p>
      ) : (
        <ul className="space-y-0.5 px-4 pb-4">
          {shown.map((st) => (
            <li key={st.key}>
              <Link
                to={st.to}
                className="group grid grid-cols-[minmax(0,9.5rem)_1fr_2.5rem] items-center gap-3 rounded px-1 py-1.5 hover:bg-neutral-50 max-md:min-h-10 sm:grid-cols-[11rem_1fr_3rem]"
              >
                <span className="truncate text-small text-neutral-700 group-hover:text-neutral-900" title={st.label2}>
                  {st.label2}
                </span>
                <span className="h-2 rounded-full bg-neutral-100" aria-hidden>
                  <span
                    className={cn(
                      'chart-grow-x block h-2 rounded-full',
                      st.key === 'CERTIFIED' ? 'bg-covered-600'
                        : st.key === 'RETEST_REQUIRED' ? 'bg-overdue-600'
                        : st.key === 'REVIEW_PENDING' ? 'bg-dueSoon-600'
                          : 'bg-primary-500',
                    )}
                    style={{ width: `${Math.round((st.count / peak) * 100)}%` }}
                  />
                </span>
                <span className="text-right text-small font-medium tabular-nums text-neutral-800">
                  {st.count.toLocaleString('en-IN')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
